import apiClient from '../apiClient';
import type { ApiResponse } from '@/types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';

/**
 * Per-company Payspot + Intervend admin endpoints. Reads return safe view
 * objects (no plaintext secrets); writes accept plaintext secrets that the
 * backend encrypts before persistence.
 *
 * When editing, send the sentinel string "__KEEP__" for a secret field the
 * user hasn't touched, so the existing value is preserved without echoing
 * it to the wire. Send empty string or null to clear it.
 */

export const SECRET_KEEP_SENTINEL = '__KEEP__';

export type TaxLabel = 'Ђ' | 'Е' | 'Г' | 'А' | 'Ж';

export interface PaymentConfigView {
  enabled: boolean;
  payspotCompanyId: string | null;
  payspotShopId: string | null;
  payspotMitShopId: string | null;
  payspotMitTerminalId: string | null;
  payspotBaseUrl: string | null;
  hasSecretKey: boolean;
  hasCallbackSecretKey: boolean;
  updatedAt: string | null;
}

export interface FiscalConfigView {
  enabled: boolean;
  intervendApiKeyIdentifier: string | null;
  intervendBaseUrl: string | null;
  topupItemName: string;
  topupTaxLabel: TaxLabel;
  hasApiKey: boolean;
  hasWebhookSecret: boolean;
  updatedAt: string | null;
}

export interface PaymentConfigInput {
  payspotCompanyId?: string | null;
  payspotShopId?: string | null;
  payspotMitShopId?: string | null;
  payspotMitTerminalId?: string | null;
  payspotSecretKey?: string | null;
  payspotCallbackSecretKey?: string | null;
  payspotBaseUrl?: string | null;
  enabled?: boolean;
}

export interface FiscalConfigInput {
  intervendApiKey?: string | null;
  intervendApiKeyIdentifier?: string | null;
  intervendBaseUrl?: string | null;
  webhookSecret?: string | null;
  topupItemName?: string;
  topupTaxLabel?: TaxLabel;
  enabled?: boolean;
}

const adminUrl = (companyId: string, suffix: string) =>
  `/admin/companies/${companyId}/${suffix}`;

// ─── Payment config ─────────────────────────────────────────────────────

export const getPaymentConfig = async (companyId: string): Promise<PaymentConfigView> => {
  const { data } = await apiClient.get<ApiResponse<PaymentConfigView>>(adminUrl(companyId, 'payment-config'));
  return data.data;
};

export const upsertPaymentConfig = async (
  companyId: string,
  body: PaymentConfigInput,
): Promise<PaymentConfigView> => {
  const { data } = await apiClient.put<ApiResponse<PaymentConfigView>>(
    adminUrl(companyId, 'payment-config'),
    body,
  );
  return data.data;
};

export const useGetPaymentConfig = (companyId: string | undefined) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_COMPANY_PAYMENT_CONFIG, companyId],
    queryFn: () => getPaymentConfig(companyId!),
    enabled: !!companyId,
  });

export const useUpsertPaymentConfig = (companyId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PaymentConfigInput) => upsertPaymentConfig(companyId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ReactQueryKeys.GET_COMPANY_PAYMENT_CONFIG, companyId] });
    },
  });
};

// ─── Fiscal config ──────────────────────────────────────────────────────

export const getFiscalConfig = async (companyId: string): Promise<FiscalConfigView> => {
  const { data } = await apiClient.get<ApiResponse<FiscalConfigView>>(adminUrl(companyId, 'fiscal-config'));
  return data.data;
};

export const upsertFiscalConfig = async (
  companyId: string,
  body: FiscalConfigInput,
): Promise<FiscalConfigView> => {
  const { data } = await apiClient.put<ApiResponse<FiscalConfigView>>(
    adminUrl(companyId, 'fiscal-config'),
    body,
  );
  return data.data;
};

export const useGetFiscalConfig = (companyId: string | undefined) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_COMPANY_FISCAL_CONFIG, companyId],
    queryFn: () => getFiscalConfig(companyId!),
    enabled: !!companyId,
  });

export const useUpsertFiscalConfig = (companyId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FiscalConfigInput) => upsertFiscalConfig(companyId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ReactQueryKeys.GET_COMPANY_FISCAL_CONFIG, companyId] });
    },
  });
};
